import JSZip from 'jszip';

/**
 * Generate a PDF document by sending a request to the specified API endpoint.
 *
 * @async
 * @function generatePDF
 * @param {Function} message - Function to display a message or handle an error.
 * @param {Function} dispatchAPI - Function to dispatch an API request.
 * @param {Object} parameters - Parameters to include in the API request body.
 * @param {string} use_case - Specific use case for generating the PDF.
 * @param {Function} setIsLoading - Function to set the loading state.
 * @param {boolean} [printPdf=true] - Whether to print the PDF or return the Blob object.
 * @returns {Promise<Blob|void>} - Returns a Blob object if `printPdf` is false, otherwise void.
 * @throws Will throw an error if the API request fails.
 *
 * @example
 * generatePDF(
 *   (msg) => console.log(msg),
 *   async (method, options) => axios({ method, ...options }),
 *   { param1: 'value1', param2: 'value2' },
 *   'use_case_example',
 *   (isLoading) => console.log('Loading:', isLoading),
 *   true
 * ).then(blob => {
 *   if (blob) {
 *     // Handle the Blob object
 *   }
 * }).catch(error => {
 *   console.error(error);
 * });
 */
/**
 * Handles PDF download/print
 * @function
 * @param {Blob} blob - The PDF blob
 * @param {boolean} printPdf - Whether to print the PDF
 * @param {boolean} openInNewTab - Whether to open in new tab
 * @param {string} downloadName - Name for download
 * @returns {void|Blob} - Returns a Blob object if `printPdf` is false, otherwise void.
 */
const handlePdfOutput = (blob, printPdf, openInNewTab, downloadName) => {
  if (!printPdf) return blob;

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;

  if (openInNewTab) {
    link.target = '_blank';
  } else {
    link.download = `${downloadName}.pdf`;
  }

  link.click();
  URL.revokeObjectURL(url);
  return null;
};

/**
 * Handles error response
 * @function
 * @param {Error} e - The error object
 * @param {Function} message - Message function
 * @param {Function} setIsLoading - Set loading state function
 * @param {Function} setVisible - Set visible state function
 * @returns {void|string} - Returns a string if the error is a 404, otherwise void.
 */
const handleErrorResponse = (e, message, setIsLoading, setVisible) => {
  if (setIsLoading) setIsLoading(false);
  if (setVisible) setVisible(false);

  if (e?.response?.status === 404) {
    return message('TEMPLATE_NOT_FOUND');
  }

  if (!e.response) {
    return message(e);
  }

  const decoder = new TextDecoder('utf-8');
  const errorText = decoder.decode(e.response.data);

  try {
    const errorData = JSON.parse(errorText);
    if (errorData.description === 'PRICE_SYSTEM_NOT_FOUND') {
      return message('PRICE_SYSTEM_NOT_FOUND');
    }
  } catch (parseError) {
    message(errorText);
  }
  return null;
};

export const generatePDF = async ({
  message,
  dispatchAPI,
  parameters,
  use_case,
  template_type,
  setIsLoading,
  printPdf = true,
  extraQuery,
  downloadName,
  exportHtml,
  openInNewTab,
  registerInDb,
  setVisible,
  translationObject
}) => {
  if (setIsLoading) setIsLoading(true);
  if (setVisible) setVisible(true);

  try {
    const { data } = await dispatchAPI('POST', {
      url: `template-documents/generatePDF${
        extraQuery ? `?${extraQuery}` : ''
      }`,
      body: {
        parameters,
        use_case,
        template_type,
        registerInDb,
        exportHtml,
        returnPdfResponse: true,
        translationObject
      },
      responseType: 'arraybuffer'
    });

    if (setIsLoading) setIsLoading(false);
    if (setVisible) setVisible(false);

    const blob = new Blob([data], { type: 'application/pdf' });
    return handlePdfOutput(blob, printPdf, openInNewTab, downloadName);
  } catch (e) {
    return handleErrorResponse(e, message, setIsLoading, setVisible);
  }
};

/**
 * Downloads multiple PDFs as a zip file.
 * @async
 * @function
 * @param {Blob[]} pdfBlobs - An array of PDF blobs to be included in the zip file.
 * @param {string} [zipFilename='documents.zip'] - The name of the zip file to be downloaded.
 * @param {string[]} [fileNames] - Optional per-blob file name, matched by index (falls back to document-N.pdf).
 * @returns {Promise<void>} - A promise that resolves when the zip file is downloaded.
 */
export const downloadPDFsAsZip = async (
  pdfBlobs,
  zipFilename = 'documents.zip',
  fileNames = []
) => {
  const zip = new JSZip();

  // Ajouter chaque PDF au fichier ZIP
  pdfBlobs.forEach((blob, index) => {
    zip.file(fileNames[index] || `document-${index + 1}.pdf`, blob);
  });

  // Générer le fichier ZIP et créer un lien de téléchargement
  const zipBlob = await zip.generateAsync({ type: 'blob' });
  const url = URL.createObjectURL(zipBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = zipFilename;
  document.body.appendChild(link);
  link.click();
  link.remove();

  // Nettoyer l'URL créée
  URL.revokeObjectURL(url);
};
